/**
 * Mission Evolution — pure helpers shared by the Mission screens
 * (Journey v26.3 addendum; aris-ai plan 41b).
 *
 * Spec rules these encode:
 * - Never show a version number. `mission.version` exists in the payload and
 *   is ignored; the API gives a label and a date, and those are what show.
 * - "Evolved" wording only — never "updated", "old", "outdated", "superseded".
 */

/** The five Mission dimensions in the design's order (payload key → label). Legacy
 * was added 2026-10-15: a Mission written before then has no `legacy` key until its
 * next Evolve, and every renderer shows only the dimensions present. */
export const MISSION_DIMENSIONS = [
  { key: "identity", label: "Character" },
  { key: "contribution", label: "Contribution" },
  { key: "relationships", label: "Relationships" },
  { key: "way_of_living", label: "Way of Living" },
  { key: "legacy", label: "Legacy" },
];

const DIMENSION_LABEL = Object.fromEntries(MISSION_DIMENSIONS.map((d) => [d.key, d.label]));

/** Label for a `changed` / `unchanged` entry ("statement" or a dimension key). */
export const missionPartLabel = (key) =>
  key === "statement" ? "Mission Statement" : DIMENSION_LABEL[key] || key;

/** "2026-10-14T09:12:00+00:00" → "Oct 14, 2026"; "" when unparseable. */
export const formatMissionDate = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const date = new Date(hasZone || !iso.includes("T") ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** `display_date_kind` / `date_kind` + date → "Created Oct 14, 2026" | "Evolved Oct 14, 2026". */
export const missionDateLine = (kind, iso) => {
  const date = formatMissionDate(iso);
  if (!date) return "";
  return `${kind === "EVOLVED" ? "Evolved" : "Created"} ${date}`;
};

/**
 * A History row's title and subtitle (contract table):
 *   CURRENT  → "Current Mission"  / "Evolved Oct 14, 2026" | "Created …"
 *   EVOLVED  → "Evolved Sep 1, 2026" / —
 *   ORIGINAL → "Original Mission" / "Created Sep 1, 2025"
 * An unknown label reads as a dated evolution, never as a version.
 */
export const historyItemTitles = (item) => {
  const line = missionDateLine(item?.date_kind, item?.date);
  switch (item?.label) {
    case "CURRENT":
      return { title: "Current Mission", subtitle: line };
    case "ORIGINAL":
      return { title: "Original Mission", subtitle: line };
    case "EVOLVED":
    default:
      return { title: line || "Evolved Mission", subtitle: "" };
  }
};

/** Client analytics (spec §20). Never send Mission text or conversation content. */
export const MISSION_EVENT = {
  EVOLVE_OPENED: "evolve_mission_opened",
  MODE_SELECTED: "mission_mode_selected",
  EVOLUTION_STARTED: "mission_evolution_started",
  DRAFT_GENERATED: "mission_draft_generated",
  REVIEW_CHANGES_OPENED: "mission_review_changes_opened",
  SAVED: "mission_saved",
  KEPT_CURRENT: "mission_kept_current",
  EVOLUTION_CANCELLED: "mission_evolution_cancelled",
  HISTORY_OPENED: "mission_history_opened",
  VERSION_VIEWED: "mission_version_viewed",
};

/** Routes for the Mission evolution screens. */
export const missionRoutes = {
  mission: "/journey/mission",
  evolve: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== "")
    ).toString();
    return `/journey/mission/evolve${qs ? `?${qs}` : ""}`;
  },
  review: (draftId) => `/journey/mission/review/${encodeURIComponent(draftId)}`,
  history: "/journey/mission/history",
  historyItem: (missionId) => `/journey/mission/history/${encodeURIComponent(missionId)}`,
};
